import { motion } from "framer-motion";
import { ExternalLink, ChevronDown, Github } from "lucide-react";
import { LOGO, CITY_SHOT_A, CITY_URL } from "@/constants/assets";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const STATS = [
  { v: "100", k: "RESIDENTS" },
  { v: "40", k: "HOUSEHOLDS" },
  { v: "68", k: "BUILDINGS" },
  { v: "2", k: "DISTRICTS" },
];

const HOOKS = ["LLM MINDS", "DETERMINISTIC KERNEL", "QUANTUM BELIEFS"];

export default function Hero() {
  return (
    <section className="relative pb-14 overflow-hidden">
      {/* video backdrop — clearly visible, dark halo only behind the text */}
      <div className="absolute inset-0" aria-hidden="true">
        <video
          src="/hero-city.mp4"
          poster={CITY_SHOT_A}
          preload="auto"
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover pixelated opacity-40"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 58% at 50% 45%, rgba(5,11,5,0.82) 0%, rgba(5,11,5,0.45) 55%, rgba(5,11,5,0.1) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-gb-bg" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 flex flex-col items-center text-center">
        <motion.img
          src={LOGO}
          alt="replicant"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="h-20 sm:h-28 w-auto mb-8"
          style={{ filter: "drop-shadow(0 0 30px rgba(64,240,112,0.35))" }}
          data-testid="hero-logo"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-3 mb-6 flex-wrap justify-center"
        >
          <span className="text-gb-neon text-xs tracking-[0.3em]">▶ A SIMULATION RESEARCH PROJECT</span>
          <span className="font-arcade text-[7px] text-gb-bg bg-gb-neon px-2 py-1">
            v0.10 · LIVE
          </span>
        </motion.div>

        <h1
          className="font-title leading-[0.82] uppercase select-none"
          style={{ filter: "drop-shadow(0 2px 16px rgba(5,11,5,0.95))" }}
        >
          <span className="mask-line">
            <motion.span
              variants={line}
              custom={0}
              initial="hidden"
              animate="show"
              className="block text-[clamp(3.2rem,8.5vw,7rem)] text-gb-pale"
            >
              A town
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span
              variants={line}
              custom={1}
              initial="hidden"
              animate="show"
              className="block text-[clamp(3.2rem,8.5vw,7rem)] text-gb-mint"
            >
              that isn't
            </motion.span>
          </span>
          <span className="mask-line">
            <motion.span
              variants={line}
              custom={2}
              initial="hidden"
              animate="show"
              className="block text-[clamp(3.2rem,8.5vw,7rem)] text-gb-neon glow-text"
            >
              real.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-4 font-pixel text-2xl sm:text-3xl text-gb-accent tracking-wide"
        >
          and yet, it lives.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-6 flex flex-wrap justify-center gap-3"
        >
          {HOOKS.map((h, i) => (
            <span
              key={h}
              data-testid={`hero-hook-${i + 1}`}
              className="border-2 border-gb-accent/50 bg-gb-bg/70 px-4 py-2 text-[10px] sm:text-[11px] tracking-[0.25em] text-gb-neon"
            >
              {h}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-6 max-w-2xl text-sm sm:text-base leading-relaxed text-gb-mint/90"
        >
          replicant simulates everyday life with LLM minds, a deterministic
          kernel, and beliefs that follow quantum probability. In our first
          simulated city, 100 residents wake up, go to school and work, meet
          friends, keep or break promises, get sick and read the paper. Every
          day is recorded, and through the city's own social network you can
          even talk with its residents.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            data-testid="hero-live-demo-cta"
            href={CITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gb-neon text-gb-bg font-bold text-xs sm:text-sm tracking-[0.18em] px-7 py-4 shadow-[0_0_30px_rgba(64,240,112,0.35)] hover:shadow-[0_0_50px_rgba(64,240,112,0.6)] hover:-translate-y-0.5 transition-all duration-200"
          >
            INTERACT WITH THE CITY LIVE
            <ExternalLink size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <div
            data-testid="hero-github-cta"
            title="Open source, coming soon"
            className="inline-flex items-center gap-3 border-2 border-dashed border-gb-accent/50 text-gb-accent/70 text-xs tracking-[0.18em] px-6 py-4 cursor-not-allowed select-none"
          >
            <Github size={15} />
            GITHUB · COMING SOON
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-5 text-[10px] tracking-[0.2em] text-gb-muted"
        >
          EVERY DAY IS RECORDED · EVERY CHOICE HAS A REASON · OPEN FOR ANYONE TO WATCH
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-10 w-full max-w-3xl grid grid-cols-2 sm:grid-cols-4 border-2 border-gb-line divide-x-2 divide-gb-line bg-gb-bg/60"
        >
          {STATS.map((s) => (
            <div key={s.k} className="px-4 py-4" data-testid={`hero-stat-${s.k.toLowerCase().replace(" ", "-")}`}>
              <div className="font-pixel text-3xl sm:text-4xl text-gb-neon leading-none">
                {s.v}
              </div>
              <div className="mt-1 text-[9px] tracking-[0.25em] text-gb-accent">{s.k}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="relative mt-12 flex justify-center">
        <div className="flex flex-col items-center gap-1 text-gb-accent/70">
          <span className="font-pixel text-sm tracking-[0.3em]">SCROLL</span>
          <ChevronDown size={16} className="animate-bob" />
        </div>
      </div>
    </section>
  );
}
